import { Box, Button, Divider, Drawer, Typography } from "@mui/material";

import {
    useGetAllResourcesQuery,
    useGetLmsResourcesQuery,
} from "@convin/redux/services/lms/lmsKnowledgeBase.service";
import CustomSkeletonLoader from "@convin/components/custom_components/Skeleton";
import {
    alpha,
    Chip,
    InputAdornment,
    TextField,
    useTheme,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useState } from "react";
import { CloseSvg } from "@convin/components/svg";
import ListItem from "./ListItem";
import { Search } from "@mui/icons-material";

const AttachedLmsDocuments = ({
    files,
    folders,
}: {
    files: number[];
    folders: number[];
}) => {
    const theme = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const { data: folderList, isLoading: isFolderListLoading } =
        useGetLmsResourcesQuery(undefined, {
            skip: folders.length === 0,
        });

    const { data: documents, isLoading: isLmsDocsLoading } =
        useGetAllResourcesQuery(
            {
                file_ext_type: [".csv", ".docx", ".md", ".pdf", ".json"],
                allow_pagination: false,
            },
            {
                skip: files.length === 0,
            }
        );

    if (isFolderListLoading || isLmsDocsLoading) {
        return <CustomSkeletonLoader rows={2} />;
    }

    const selectedFiles =
        documents
            ?.filter((e) => files.includes(e.id))
            .map((e) => ({
                name: e.file_name,
                type: "file",
                id: e.id,
            })) ?? [];
    const selectedFolders =
        folderList?.results
            ?.filter((e) => folders.includes(e.id))
            .map((e) => ({
                name: e.name,
                type: "folder",
                id: e.id,
            })) ?? [];
    const defaultItemRenderer = (
        item: string,
        index: number,
        type: "file" | "folder"
    ) => (
        <Chip
            key={index}
            label={
                <div className="flex items-center gap-2">
                    {type === "file" ? (
                        <InsertDriveFileOutlinedIcon
                            sx={{ height: 16, width: 16 }}
                        />
                    ) : (
                        <FolderOutlinedIcon sx={{ height: 16, width: 16 }} />
                    )}
                    {item}
                </div>
            }
            variant="filled"
            sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontSize: theme.typography.pxToRem(14),
            }}
            onClick={type === "folder" ? () => setDrawerOpen(true) : undefined}
        />
    );

    const totalFilesAndFolders = [...selectedFolders, ...selectedFiles] as {
        name: string;
        type: "file" | "folder";
        id: number;
    }[];
    const visibleItems = totalFilesAndFolders.slice(0, 4);
    const hiddenItems =
        totalFilesAndFolders.length > visibleItems.length
            ? totalFilesAndFolders.length - visibleItems.length
            : 0;
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    overflow: "hidden",
                }}
            >
                {visibleItems.map((item, index) => (
                    <>
                        {defaultItemRenderer(
                            item.name,
                            index,
                            item.type as "file" | "folder"
                        )}
                    </>
                ))}
                {hiddenItems > 0 && (
                    <Chip
                        label={`+${hiddenItems}`}
                        variant="outlined"
                        component="button"
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            fontSize: theme.typography.pxToRem(14),
                        }}
                    />
                )}
            </Box>
            <Drawer
                anchor="right"
                open={drawerOpen}
                sx={{
                    zIndex: 10000,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: "276px",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        p={1.5}
                        className="flex justify-between items-center flex-shrink-0"
                    >
                        <Typography variant="h6">Attached Files</Typography>
                        <Button
                            variant="text"
                            sx={{
                                color: "textColors.333",
                            }}
                            onClick={() => setDrawerOpen(false)}
                        >
                            <CloseSvg />
                        </Button>
                    </Box>
                    <Divider />
                    <Box
                        p={1.5}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexWrap: "wrap",
                            gap: 1,
                            flex: 1,
                            overflow: "auto",
                        }}
                    >
                        <div className="flex-shrink-0">
                            <TextField
                                fullWidth
                                placeholder="Search File"
                                value={searchQuery}
                                sx={{
                                    ".MuiInputBase-input": {
                                        p: 1.5,
                                        py: 0.5,
                                        fontSize: theme.typography.pxToRem(14),
                                    },
                                    backgroundColor: alpha(
                                        theme.palette.textColors[999],
                                        0.1
                                    ),
                                }}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    m: "0px !important",
                                                }}
                                            >
                                                <Search
                                                    sx={{
                                                        color: "textColors.999",
                                                        height: 16,
                                                        width: 16,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                        <div className="flex-1 overflow-auto flex flex-col gap-3">
                            {totalFilesAndFolders.map((item) => {
                                if (
                                    item.name
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())
                                ) {
                                    return (
                                        <ListItem key={item.id} item={item} />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default AttachedLmsDocuments;
